import React, {useEffect, useMemo, useState} from 'react';
import {Box, Typography, Button, CircularProgress} from '@mui/material';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {useHistory, useLocation} from 'react-router-dom';
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer} from 'recharts';
import SchoolDashboardShell from '../components/SchoolDashboard.comp';
import {NAVY, TEAL, PageHeader, StatCard, BandChip} from '../components/widgets.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {getProgramImpactDashboardService} from '../../../services/dynamicDashboard.service';
import {findCard, rowsFrom} from '../schoolData';
import {PROGRAM_IMPACT_SECTIONS, PASS_THRESHOLD, kpisFrom, classRowsFrom, programImpactMetaFrom} from '../../DashboardCommon/programImpact';
import {ClassPassRateCard} from '../../DashboardCommon/ClassPassRateCard.comp';
import {FEATURE_KEY} from '../../../utils/constants';

const LEARNING_IMPACT_PATH = '/dq_pro/school-dashboard/learning-programs/learning-impact';

const PRE_COLOR = '#A9B4D4';
const POST_COLOR = TEAL;

/* Slides 44–46 — Program Impact, one page per deployed program, reached from
   Learning Impact's "View Impact" (?program_code=<module_code>).

   Everything is read, nothing is made up:
   - the program list is the school's own Learning Impact tracker (SD.S_19.C_2);
   - the selected program's figures are its Program Impact dashboard
     (P1 Data Map PID.S_1 / PID.S_2), fetched by module_code and scoped by the
     API to the user's own school.
   Curriculum Coverage (SD-44-05) is not shown: the Data Map drops it (D-10).
   The card readers live in DashboardCommon/programImpact (shared with Teacher PID). */
const InfoPill = ({children}) => (
    <Box sx={{
        border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px',
        backgroundColor: 'var(--brand-surface, #fff)', px: 2, py: 1,
        fontSize: '13px', color: '#374151', whiteSpace: 'nowrap',
    }}>
        {children}
    </Box>
);

const LEARNING_IMPACT_SECTION_CODE = 'S_19';

// A response without card sections reads as empty cards (same as before the readers moved).
const NO_SECTIONS = {overviewCards: [], byClassCards: [], meta: null, classTable: null, schoolAvgPost: null};

const ProgramImpactPage = () => {
    const history = useHistory();
    const location = useLocation();
    const {dashboardData, loading} = useDashboard({ card_sections: LEARNING_IMPACT_SECTION_CODE });

    // The school's programs, from its Learning Impact tracker (SD.S_19.C_2).
    const programs = useMemo(() => rowsFrom(findCard(dashboardData, LEARNING_IMPACT_SECTION_CODE, 'C_2'), {name: 'Program'})
        .map((r) => ({name: r.name, code: r._cell.name?.module_code}))
        .filter((p) => p.code), [dashboardData]);

    const selectedCode = new URLSearchParams(location.search).get('program_code') || programs[0]?.code || null;
    const listed = programs.find((p) => p.code === selectedCode);

    // The selected program's Program Impact dashboard (PID), by module_code.
    const [impact, setImpact] = useState({data: null, loading: false});
    useEffect(() => {
        if (!selectedCode) return undefined;
        let cancelled = false;
        setImpact({data: null, loading: true});
        getProgramImpactDashboardService({module_code: selectedCode, card_sections: PROGRAM_IMPACT_SECTIONS.join(',')})
            .then((res) => { if (!cancelled) setImpact({data: res?.data || null, loading: false}); })
            .catch((e) => {
                console.warn('[program impact] could not load:', e?.message || e);
                if (!cancelled) setImpact({data: null, loading: false});
            });
        return () => { cancelled = true; };
    }, [selectedCode]);

    const selectProgram = (code) => {
        history.push(`${location.pathname}?program_code=${encodeURIComponent(code)}`);
    };

    const {overviewCards, meta, classTable, schoolAvgPost} = programImpactMetaFrom(impact.data) || NO_SECTIONS;
    const stats = kpisFrom(overviewCards);
    const byClass = classRowsFrom(classTable);
    const chartData = byClass.filter((c) => !c.suppressed && (c.pre !== null || c.post !== null)).map((c) => ({grade: c.label, Pre: c.pre, Post: c.post}));
    const programName = meta?.program_name || meta?.title || listed?.name || '';
    const sessionsLabel = meta?.session_count
        ? `${meta.session_count} session${meta.session_count === 1 ? '' : 's'}${meta.minutes_per_session ? ` × ${meta.minutes_per_session} min` : ''}`
        : null;

    return (
        <SchoolDashboardShell skipNoDataGate>
            <Button
                startIcon={<ArrowBackIcon fontSize="small"/>}
                onClick={() => history.push(LEARNING_IMPACT_PATH)}
                sx={{textTransform: 'none', fontWeight: 700, color: NAVY, mb: 1.5, pl: 0, '&:hover': {backgroundColor: 'transparent', textDecoration: 'underline'}}}
            >
                Back to Learning Impact
            </Button>

            <PageHeader title={programName ? `Program Impact: ${programName}` : 'Program Impact'}/>

            {/* SD-44-01 — signal badge + evidence confidence (PID.S_1.C_1). */}
            {meta && (
                <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25, mb: 2.5}}>
                    {meta.signal && <BandChip label={meta.signal} color={TEAL}/>}
                    {meta.ec_band && <InfoPill><strong>Evidence confidence:</strong>&nbsp;{meta.ec_band}</InfoPill>}
                    {!meta.signal && meta.message && (
                        <Typography sx={{fontSize: '13px', color: '#6A7282'}}>{meta.message}</Typography>
                    )}
                </Box>
            )}

            <Box sx={{display: 'flex', gap: 2.5, alignItems: 'stretch', flexDirection: {xs: 'column', md: 'row'}}}>
                <Box sx={{
                    width: {xs: '100%', md: '300px'}, flexShrink: 0,
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    p: 2.5, display: 'flex', flexDirection: 'column',
                }}>
                    <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5}}>
                        Select a Program
                    </Typography>
                    <Typography sx={{fontSize: '12.5px', color: '#9CA3AF', mb: 1.5}}>
                        Choose one program to view its impact.
                    </Typography>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                        {(loading && !programs.length) && <CircularProgress size={20}/>}
                        {!loading && !programs.length && (
                            <Typography sx={{fontSize: '13px', color: '#6A7282'}}>No deployed programs yet.</Typography>
                        )}
                        {programs.map((p) => {
                            const active = selectedCode === p.code;
                            return (
                                <Box
                                    key={p.code}
                                    onClick={() => selectProgram(p.code)}
                                    sx={{
                                        cursor: 'pointer', borderRadius: '8px', px: 1.25, py: 0.75,
                                        backgroundColor: active ? 'rgba(255, 105, 0, 0.08)' : 'transparent',
                                        '&:hover': {backgroundColor: active ? 'rgba(255, 105, 0, 0.08)' : 'rgba(0, 0, 0, 0.03)'},
                                    }}
                                >
                                    <Typography sx={{fontSize: '13.5px', color: active ? NAVY : '#374151', fontWeight: active ? 700 : 400, lineHeight: 1.4}}>
                                        {p.name}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{flex: 1, minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column'}}>
                    {impact.loading && <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}><CircularProgress/></Box>}
                    {!impact.loading && selectedCode && !impact.data && (
                        <Typography sx={{fontSize: '14px', color: '#6A7282', py: 4}}>
                            Impact data for this program is not available yet.
                        </Typography>
                    )}
                    {!impact.loading && impact.data && (<>
                    {/* SD-44-02 — program meta chips (PID.S_1.C_1). */}
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5}}>
                        <InfoPill><strong>Target Area:</strong>&nbsp;{meta?.target_area_label || '—'}</InfoPill>
                        {sessionsLabel && <InfoPill>{sessionsLabel}</InfoPill>}
                        {meta && <InfoPill>{meta.learners ?? '—'} learners · {meta.classes_assigned ?? '—'} / {meta.classes_total ?? '—'} classes</InfoPill>}
                    </Box>

                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)'}, gap: 2, mb: 3}}>
                        {stats.map((s) => (
                            <StatCard key={s.label} label={s.label} value={s.value} sub={s.sub}/>
                        ))}
                    </Box>

                    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2.5, alignItems: 'stretch'}}>
                        <Box sx={{
                            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                            backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                            p: 3, display: 'flex', flexDirection: 'column',
                        }}>
                            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 0.5}}>
                                Pre → Post by Class
                            </Typography>
                            <Typography sx={{fontSize: '13px', color: '#6A7282', mb: 2}}>
                                {schoolAvgPost !== null ? <>School avg post <strong style={{color: NAVY}}>{Math.round(schoolAvgPost)}</strong> · </> : null}{stats[2].value} impact
                            </Typography>
                            {!chartData.length && (
                                <Typography sx={{fontSize: '13px', color: '#6A7282'}}>No pre and post scores yet.</Typography>
                            )}
                            {chartData.length > 0 && <Box sx={{flexGrow: 1, minHeight: 260, width: '100%'}}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{top: 10, right: 10, left: -20, bottom: 0}}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6"/>
                                        <XAxis dataKey="grade" tick={{fontSize: 11, fill: '#6B7280'}} stroke="#E5E7EB"/>
                                        <YAxis tick={{fontSize: 11, fill: '#6B7280'}} stroke="#E5E7EB"/>
                                        <Tooltip/>
                                        <Legend wrapperStyle={{fontSize: 12}}/>
                                        <Bar dataKey="Pre" fill={PRE_COLOR} radius={[4, 4, 0, 0]}/>
                                        <Bar dataKey="Post" fill={POST_COLOR} radius={[4, 4, 0, 0]}/>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>}
                        </Box>

                        <ClassPassRateCard byClass={byClass}/>
                    </Box>

                    <Typography sx={{fontSize: '12px', color: '#9CA3AF', mt: 2.5, lineHeight: 1.6}}>
                        * Pass rate = share of learners scoring {PASS_THRESHOLD} or above on the post-assessment. Impact rate
                        = gap closure = (Post − Pre) ÷ (100 − Pre) × 100.
                    </Typography>
                    </>)}
                </Box>
            </Box>
        </SchoolDashboardShell>
    );
};

export default ProgramImpactPage;
